import { useForm } from 'react-hook-form';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from 'components/Input/Input';
import { BankIcon } from 'components/Icons/BankIcon';
import { CheckIcon } from 'components/Icons/CheckIcon';
import { InfoFilledIcon } from 'components/Icons/InfoFilledIcon';
import {
  CompleteRegShieldRegistration,
  OnRegShieldError,
  OnRegShieldRedirectChallenge
} from 'components/RegShield/types';
import { BankAutoComplete } from 'components/HomePage/BankAutoComplete';
import { Bank, BankLoginCredentials } from 'models/Bank';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { RegShieldWidget } from 'components/RegShield/RegShieldWidget';
import { Button } from 'components/Button/Button';
import { ArrowIcon } from 'components/Icons/Arrow';
import {
  CreateDepotTransfers,
  CreateDepotTransfersState,
  CreateFullDepotTransfers,
  CreateRegShieldDepotTransfers,
  SearchBanks,
  SelectBank
} from 'components/HomePage/types';
import { Switch } from 'components/Switch/Switch';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Tenant } from 'models/Tenant';
import { Markdown } from 'components/Markdown/Markdown';

export interface HomePageProps {
  banks?: Bank[];
  onRegShieldFinish: CompleteRegShieldRegistration;
  onRegShieldError: OnRegShieldError;
  onRegShieldRedirectChallenge: OnRegShieldRedirectChallenge;
  selectedBank?: Bank;
  bankLoginCredentials?: BankLoginCredentials;
  selectBank: SelectBank;
  searchBanks: SearchBanks;
  regShieldLocation?: string;
  isRegShieldRegistrationComplete: boolean;
  createDepotTransfers: CreateDepotTransfers;
  createFullDepotTransfers: CreateFullDepotTransfers;
  createRegShieldDepotTransfers: CreateRegShieldDepotTransfers;
  tenant: Tenant;
  isLoading: boolean;
}

export const HomePage = ({
  banks = [],
  bankLoginCredentials,
  selectedBank,
  onRegShieldFinish,
  onRegShieldError,
  onRegShieldRedirectChallenge,
  regShieldLocation,
  isRegShieldRegistrationComplete,
  createDepotTransfers,
  createFullDepotTransfers,
  createRegShieldDepotTransfers,
  searchBanks,
  selectBank,
  tenant,
  isLoading
}: HomePageProps) => {
  const [isFullStocksTransfer, setIsFullStocksTransfer] = useState(true);
  const currentBankLoginCredentials = useRef<BankLoginCredentials | undefined>(
    bankLoginCredentials
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    unregister,
    clearErrors,
    getValues,
    reset: resetForm
  } = useForm<CreateDepotTransfersState>({
    defaultValues: {
      newBank: {
        name: tenant.bank_name,
        depot: ''
      },
      terms: {
        privacy: false
      }
    }
  });

  useEffect(() => {
    const { oldBank } = getValues();
    const shouldUnregisterFields =
      oldBank &&
      bankLoginCredentials !== currentBankLoginCredentials.current &&
      currentBankLoginCredentials.current !== undefined;

    if (shouldUnregisterFields) {
      const fieldsToRemove = Object.keys(oldBank).map(
        (key): `oldBank.${string}` => `oldBank.${key}`
      );

      unregister(fieldsToRemove);
      clearErrors(fieldsToRemove);
    }

    currentBankLoginCredentials.current = bankLoginCredentials;
    resetForm();
  }, [bankLoginCredentials, clearErrors, getValues, unregister, resetForm]);

  useEffect(() => {
    setIsFullStocksTransfer(true);
  }, [selectedBank]);

  const onFormSubmit = (data: CreateDepotTransfersState) => {
    const { sort_code: sortCode } = selectedBank ?? {};

    if (!sortCode) return;

    if (isFullStocksTransfer) {
      createFullDepotTransfers({
        newBank: data.newBank,
        sortCode
      });
    } else if (demo_supported) {
      createDepotTransfers({ ...data, sortCode });
    } else if (regshield_supported) {
      createRegShieldDepotTransfers({
        ...data,
        sortCode
      });
    }
  };

  const onIsFullStocksTransferChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setIsFullStocksTransfer(checked);
    clearErrors();
  };

  const { demo_supported, regshield_supported, offline_supported } = selectedBank ?? {};

  const shouldRenderDemoLoginCredentials =
    bankLoginCredentials && demo_supported && !isFullStocksTransfer;

  const shouldEnableNewBankFields =
    (demo_supported && bankLoginCredentials) ||
    (selectedBank && (isFullStocksTransfer || isRegShieldRegistrationComplete));

  const shouldShowTermsCheckbox = demo_supported || (selectedBank && isFullStocksTransfer);

  const shouldShowRegShieldWidget =
    !isRegShieldRegistrationComplete &&
    regshield_supported &&
    regShieldLocation &&
    !isFullStocksTransfer;

  const shouldShowRegShieldCompleteIcon =
    regshield_supported && isRegShieldRegistrationComplete && !isFullStocksTransfer;

  const isUnsupportedBank = (!demo_supported && !regshield_supported) || offline_supported;

  const shouldShowUnsupportedBankText = selectedBank && !isFullStocksTransfer && isUnsupportedBank;

  const newBankLogoUrl = tenant.right_box_logo_url || tenant.logo_url;

  const { t } = useTranslation();

  return (
    <>
      <h1 className="mb-10 text-center" data-testid="home-page-title">
        {t('login_form.welcome')}
      </h1>
      <div className="flex min-h-[475px] w-full max-w-[900px] basis-auto">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          noValidate
          className="flex w-full basis-full flex-col justify-evenly gap-4 md:flex-row md:gap-4"
        >
          <div className="flex basis-6/12 flex-col p-4 shadow-container">
            <div className="mb-4 flex flex-col items-center">
              <h3 className="mb-2" data-testid="home-page-old-bank-title">
                {t('login_form.old_bank.title')}
              </h3>
              <BankIcon
                height={64}
                width={64}
                className="text-graySecondary"
                data-testid="home-page-old-bank-icon"
              />
            </div>
            <BankAutoComplete banks={banks} onInputChange={searchBanks} onBankSelect={selectBank} />
            {selectedBank && (
              <div className="mb-3 [&>label>div]:w-full [&>label]:w-full">
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="isFullStocksTransfer" data-testid="is-full-stocks-transfer-label">
                    {t('login_form.is_full_stocks_transfer')}
                  </label>
                  <Tooltip
                    content={t('login_form.is_full_stocks_transfer_tooltip')}
                    id="isFullStocksTransferTooltip"
                  />
                </div>
                <Switch
                  name="isFullStocksTransfer"
                  id="isFullStocksTransfer"
                  onChange={onIsFullStocksTransferChange}
                  checked={isFullStocksTransfer}
                />
              </div>
            )}
            {shouldShowRegShieldCompleteIcon && (
              <CheckIcon
                data-testid="regshield-completed-icon"
                height={48}
                width={48}
                className="mx-auto flex h-full min-h-[100px] animate-fadeOpacity items-center justify-center text-success"
              />
            )}
            {shouldShowRegShieldWidget && (
              <div className="animate-fadeOpacity">
                <RegShieldWidget
                  location={regShieldLocation}
                  onRegShieldFinish={onRegShieldFinish}
                  onRegShieldError={onRegShieldError}
                  onRegShieldRedirectChallenge={onRegShieldRedirectChallenge}
                />
              </div>
            )}
            {shouldRenderDemoLoginCredentials && (
              <div className="animate-fadeOpacity">
                {bankLoginCredentials.fields.map(({ name, title, masked }) => {
                  return (
                    <Input
                      label={title}
                      id={name}
                      type={masked ? 'password' : 'text'}
                      key={name}
                      errorText={errors?.oldBank?.[name]?.message}
                      {...register(`oldBank.${name}`, {
                        required: t('login_form.error.please_fill')
                      })}
                    />
                  );
                })}
              </div>
            )}
            {shouldShowUnsupportedBankText && (
              <div className="border border-error p-4">
                <span data-testid="unsupported-bank-text">
                  {`${t('login_form.error.unsupported_bank')}`}
                </span>
                {tenant.additional_unsupported_info && (
                  <Markdown
                    data-testid="additional-unsupported-bank-text"
                    text={tenant.additional_unsupported_info}
                  />
                )}
              </div>
            )}
          </div>
          <div className="relative">
            <div className="absolute -top-8 left-[49%] ml-[-25px] h-[60px] w-[60px] items-center justify-center rounded-[50%] bg-black text-center leading-[50px] text-[white] md:left-[-23px] md:top-[23px] md:flex md:h-[94px] md:w-[94px] md:text-[42px] md:leading-[94px]">
              <ArrowIcon
                className="h-[57px] w-[60px] rotate-180 text-white md:rotate-90"
                viewBox="0 2 24 24"
              />
            </div>
          </div>
          <div className="flex basis-6/12 flex-col p-4 shadow-container">
            <div className="mb-4 flex flex-col items-center">
              <h3 className="mb-2" data-testid="home-page-new-bank-title">
                {t('login_form.new_bank.title')}
              </h3>
              {newBankLogoUrl ? (
                <div className="flex h-[64px] items-center">
                  <img
                    alt={`${tenant.bank_name} logo`}
                    className="max-h-full"
                    data-testid="home-page-new-bank-logo"
                    src={newBankLogoUrl}
                  />
                </div>
              ) : (
                <BankIcon
                  height={64}
                  width={64}
                  className="text-graySecondary"
                  data-testid="home-page-new-bank-icon"
                />
              )}
            </div>
            <Input
              label={t('login_form.new_bank.title')}
              id="newBank.name"
              disabled
              placeholder={tenant.bank_name}
              errorText={errors?.newBank?.name?.message}
              {...register('newBank.name')}
            />
            <Input
              label={t('login_form.new_bank.depot_number_label', { new_bank: tenant.bank_name })}
              id="newBank.depot"
              disabled={!shouldEnableNewBankFields}
              placeholder="12345678"
              errorText={errors?.newBank?.depot?.message}
              {...register('newBank.depot', {
                required: t('login_form.error.please_fill'),
                pattern: {
                  value: new RegExp(tenant.depot_number_regex ?? ''),
                  message: t('login_form.error.number_not_valid')
                }
              })}
              className="[&>label]:capitalize"
            />

            {shouldEnableNewBankFields && (
              <div className="animate-fadeOpacity">
                {shouldShowTermsCheckbox && (
                  <Checkbox
                    id="terms.privacy"
                    errorText={errors?.terms?.privacy?.message}
                    {...register('terms.privacy', { required: t('login_form.error.please_mark') })}
                  >
                    <Markdown
                      text={t('login_form.new_bank.checkbox_terms')}
                      htmlTag="div"
                      className="[&>p>a]:underline "
                    />
                  </Checkbox>
                )}
                <Button
                  type="submit"
                  name="submitLogin"
                  className="mt-4 w-full"
                  data-testid="button-submit-create-depot-transfers"
                  disabled={isSubmitting || isLoading}
                >
                  <span className="w-full text-center">{t('login_form.start')}</span>
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>

      <div className="mt-10 flex max-w-[900px] flex-col items-center bg-white">
        <div className="mb-4 flex max-w-full basis-12 flex-col">
          <h3
            className="flex items-center justify-center text-center uppercase"
            data-testid="home-page-info-title"
          >
            <InfoFilledIcon
              className="mr-2 text-yellow-500"
              data-testid="home-page-info-title-icon"
            />
            {t('login_form.before_you_start')}
          </h3>
          <span className="text-center text-sm text-gray-600" data-testid="home-page-info-subtitle">
            {t('login_form.collect_information')}
          </span>
        </div>
        <div className="flex flex-col items-stretch justify-evenly gap-8 md:flex-row md:gap-4">
          <div className="flex basis-3/12 items-start justify-center">
            <div className="flex flex-col items-center md:items-stretch">
              <div className="flex">
                <CheckIcon className="mr-2" data-testid="home-page-info-section-1-title-icon" />{' '}
                <h3 className="flex uppercase" data-testid="home-page-info-section-1-title">
                  {t('login_form.required')}
                </h3>
              </div>
              <h4
                className="md:pl-[calc(24px+0.5rem)]"
                data-testid="home-page-info-section-1-subtitle"
              >
                {t('login_form.personal_data.title')}
              </h4>
              <span
                className="text-center text-sm text-gray-600 md:pl-[calc(24px+0.5rem)] md:text-start"
                data-testid="home-page-info-section-1-text"
              >
                {t('login_form.personal_data.description')}
              </span>
            </div>
          </div>

          <div className="flex basis-3/12 items-start justify-center">
            <div className="flex flex-col items-center md:items-stretch">
              <div className="flex">
                <CheckIcon className="mr-2" data-testid="home-page-info-section-2-title-icon" />{' '}
                <h3 className="flex uppercase" data-testid="home-page-info-section-2-title">
                  {t('login_form.also_required')}
                </h3>
              </div>
              <h4
                className="md:pl-[calc(24px+0.2rem)]"
                data-testid="home-page-info-section-2-subtitle"
              >
                {t('login_form.joined_account.title')}
              </h4>
              <span
                className="text-center text-sm text-gray-600 md:pl-[calc(24px+0.2rem)] md:text-start"
                data-testid="home-page-info-section-2-text "
              >
                {t('login_form.joined_account.description')}
              </span>
            </div>
          </div>

          <div className="flex basis-3/12 items-start justify-center">
            <div className="flex flex-col items-center md:items-stretch">
              <div className="flex">
                <CheckIcon className="mr-2" data-testid="home-page-info-section-3-title-icon" />{' '}
                <h3 className="flex uppercase" data-testid="home-page-info-section-3-title">
                  {t('login_form.also_required')}
                </h3>
              </div>
              <h4
                className="md:pl-[calc(24px+0.2rem)]"
                data-testid="home-page-info-section-3-subtitle"
              >
                {t('login_form.account_receiver.title')}
              </h4>
              <span
                className="text-center text-sm text-gray-600 md:pl-[calc(24px+0.2rem)] md:text-start"
                data-testid="home-page-info-section-3-text"
              >
                {t('login_form.account_receiver.description')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
